import "@/i18n";
import "@/telemetry";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Erreurs déjà gérées (upsell, 2FA) : apiError renvoie "" → pas de toast vide.
const _toastError = toast.error;
toast.error = (msg, opts) => (msg ? _toastError(msg, opts) : undefined);
import { applyDocumentBranding } from "@/lib/branding";

import { AuthProvider } from "@/context/AuthContext";
import { StaffProvider } from "@/context/StaffContext";
import { ProtectedRoute, StaffProtectedRoute } from "@/components/ProtectedRoute";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AcceptInvite from "@/pages/AcceptInvite";
import Welcome from "@/pages/Welcome";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import AuditLog from "@/pages/AuditLog";
import PlanPage from "@/pages/PlanPage";
import InvoicesList from "@/pages/InvoicesList";
import InvoicesHub from "@/pages/InvoicesHub";
import SalesSettingsHub from "@/pages/SalesSettingsHub";
import SalesDocumentEditor from "@/pages/SalesDocumentEditor";
import SalesInvoiceImportUpload from "@/pages/SalesInvoiceImportUpload";
import RebillingWizard from "@/pages/RebillingWizard";
import ImportVentesBatch from "@/pages/ImportVentesBatch";
import ImportJobs from "@/pages/ImportJobs";
import HistoriqueVentes from "@/pages/HistoriqueVentes";
import RecurringInvoices from "@/pages/RecurringInvoices";
import Suppliers from "@/pages/Suppliers";
import SuppliersDedup from "@/pages/SuppliersDedup";
import SupplierDetail from "@/pages/SupplierDetail";
import HistoriqueAchats from "@/pages/HistoriqueAchats";
import ImportAchatsBatch from "@/pages/ImportAchatsBatch";
import VigilanceAchats from "@/pages/VigilanceAchats";
import Contracts from "@/pages/Contracts";
import ContratsDashboard from "@/pages/ContratsDashboard";
import ContratsEcheances from "@/pages/ContratsEcheances";
import ContratsFinancier from "@/pages/ContratsFinancier";
import ContratsEvolution from "@/pages/ContratsEvolution";
import ContratsAnnuaire from "@/pages/ContratsAnnuaire";
import ContratsPortefeuille from "@/pages/ContratsPortefeuille";
import ContratsParametres from "@/pages/ContratsParametres";
import Products from "@/pages/Products";
import NumberingSettings from "@/pages/NumberingSettings";
import SalesDefaultsSettings from "@/pages/SalesDefaultsSettings";
import HRDashboard from "@/pages/HRDashboard";
import Salaries from "@/pages/Salaries";
import SalarieDetail from "@/pages/SalarieDetail";
import Absences from "@/pages/Absences";
import Advances from "@/pages/Advances";
import AdvancesV2 from "@/pages/AdvancesV2";
import AdvanceConfigs from "@/pages/AdvanceConfigs";
import HRSettings from "@/pages/HRSettings";
import PayrollMatrix from "@/pages/PayrollMatrix";
import HRTrash from "@/pages/HRTrash";
import PayslipsOverview from "@/pages/PayslipsOverview";
import PayslipsBulkImport from "@/pages/PayslipsBulkImport";
import ContractsOverview from "@/pages/ContractsOverview";
import RegistreUniqueDuPersonnel from "@/pages/RegistreUniqueDuPersonnel";
import ComplianceAlerts from "@/pages/ComplianceAlerts";
import ContractIntake from "@/pages/ContractIntake";
import ContratsCorbeille from "@/pages/ContratsCorbeille";
import NotificationSettings from "@/pages/NotificationSettings";
import ContratsConformite from "@/pages/ContratsConformite";
import ContratsBulkImport from "@/pages/ContratsBulkImport";
import ContratsChatCFO from "@/pages/ContratsChatCFO";
import ContratsCompare from "@/pages/ContratsCompare";
import ContratsAnomalies from "@/pages/ContratsAnomalies";
import ContratsIndexation from "@/pages/ContratsIndexation";
import Resiliations from "@/pages/Resiliations";
import Comptabilite from "@/pages/Comptabilite";
import Depenses from "@/pages/Depenses";
import Pilotage from "@/pages/Pilotage";
import BoardPack from "@/pages/BoardPack";
import TasksKanban from "@/pages/TasksKanban";
import DroitSocietes from "@/pages/DroitSocietes";
import EngagementsContrats from "@/pages/engagements/EngagementsContrats";
import EngagementContratDetail from "@/pages/engagements/EngagementContratDetail";
import EngagementImport from "@/pages/engagements/EngagementImport";
import EngagementsCalendrier from "@/pages/engagements/EngagementsCalendrier";
import EngagementsResiliationsList from "@/pages/engagements/EngagementsResiliationsList";
import EngagementsParametres from "@/pages/engagements/EngagementsParametres";
import ComptabiliteDossier from "@/pages/ComptabiliteDossier";
import ComptabiliteParametres from "@/pages/ComptabiliteParametres";
import { FleetDashboard, VehiclesList, VehicleDetail } from "@/pages/Fleet";
import FleetClaims from "@/pages/FleetClaims";
import FleetContractsOverview from "@/pages/FleetContractsOverview";
import FleetCostsSummary from "@/pages/FleetCostsSummary";
import FleetTrash from "@/pages/FleetTrash";
import FleetFines from "@/pages/FleetFines";
import FleetMessageTemplates from "@/pages/FleetMessageTemplates";
import HRPersons from "@/pages/HRPersons";
import ContractDetail from "@/pages/ContractDetail";
import ContractsHub from "@/pages/ContractsHub";
import ContractsQualityHub from "@/pages/ContractsQualityHub";
import ClientRenewals from "@/pages/ClientRenewals";
import VigilanceContrats from "@/pages/VigilanceContrats";
import InvoiceView from "@/pages/InvoiceView";
import PurchasesList from "@/pages/PurchasesList";
import PurchasesHub from "@/pages/PurchasesHub";
import SupplierInvoiceUpload from "@/pages/SupplierInvoiceUpload";
import SupplierInvoiceView from "@/pages/SupplierInvoiceView";
import ExpenseReportsList from "@/pages/ExpenseReportsList";
import ExpenseReportEditor from "@/pages/ExpenseReportEditor";
import ExpenseReportView from "@/pages/ExpenseReportView";
import ThresholdsSettings from "@/pages/ThresholdsSettings";
import TenantSettings from "@/pages/TenantSettings";
import PendingActions from "@/pages/PendingActions";
import Treasury from "@/pages/Treasury";
import ReminderSettings from "@/pages/ReminderSettings";
import BackupAlerts from "@/pages/BackupAlerts";
import SecuritySettings from "@/pages/SecuritySettings";
import TenantSecurity from "@/pages/TenantSecurity";
import Entities from "@/pages/Entities";
import Connectors from "@/pages/Connectors";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import SalesOverview from "@/pages/SalesOverview";
import PurchasesOverview from "@/pages/PurchasesOverview";
import Quotes from "@/pages/Quotes";
import AnalyseCA from "@/pages/AnalyseCA";
import Vigilance from "@/pages/Vigilance";
import StaffLogin from "@/pages/StaffLogin";
import StaffPortal from "@/pages/StaffPortal";
import GlobalUpsellHandler from "@/components/GlobalUpsellHandler";
import ApiSettings from "@/pages/ApiSettings";
import ClarixAI from "@/pages/ClarixAI";
import Reconciliation from "@/pages/Reconciliation";
import Notifications from "@/pages/Notifications";

function App() {
  useEffect(() => { applyDocumentBranding(); }, []);
  return (
    <BrowserRouter>
      <StaffProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/welcome" replace />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />

            {/* Tenant workspace */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="members" element={<Members />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="plan" element={<PlanPage />} />
              <Route path="sales" element={<SalesOverview />} />
              <Route path="invoices" element={<InvoicesHub />} />
              <Route path="invoices/products" element={<Products />} />
              <Route path="invoices/settings" element={<SalesSettingsHub />} />
              <Route path="hr" element={<HRDashboard />} />
              <Route path="hr/salaries" element={<Salaries />} />
              <Route path="hr/employees/:id" element={<SalarieDetail />} />
              <Route path="hr/absences" element={<Absences />} />
              <Route path="hr/advances" element={<AdvancesV2 />} />
              <Route path="hr/advances-legacy" element={<Advances />} />
              <Route path="hr/advances-configs" element={<AdvanceConfigs />} />
              <Route path="hr/settings" element={<HRSettings />} />
              <Route path="hr/payroll" element={<PayrollMatrix />} />
              <Route path="hr/payslips" element={<PayslipsOverview />} />
              <Route path="hr/payslips/bulk-import" element={<PayslipsBulkImport />} />
              <Route path="hr/trash" element={<HRTrash />} />
              <Route path="hr/contracts-overview" element={<ContractsOverview />} />
              <Route path="hr/rup" element={<RegistreUniqueDuPersonnel />} />
              <Route path="hr/alerts" element={<ComplianceAlerts />} />
              <Route path="fleet" element={<FleetDashboard />} />
              <Route path="fleet/vehicles" element={<VehiclesList />} />
              <Route path="fleet/vehicles/:id" element={<VehicleDetail />} />
              <Route path="fleet/claims" element={<FleetClaims />} />
              <Route path="fleet/contracts" element={<FleetContractsOverview />} />
              <Route path="fleet/costs-summary" element={<FleetCostsSummary />} />
              <Route path="fleet/trash" element={<FleetTrash />} />
              <Route path="fleet/fines" element={<FleetFines />} />
              <Route path="fleet/settings" element={<FleetMessageTemplates />} />
              <Route path="hr/persons" element={<HRPersons />} />
              <Route path="resiliations" element={<Resiliations />} />
              <Route path="accounting" element={<Comptabilite />} />
              <Route path="accounting/folders/:id" element={<ComptabiliteDossier />} />
              <Route path="accounting/settings" element={<ComptabiliteParametres />} />
              <Route path="depenses" element={<Depenses />} />
              <Route path="pilotage" element={<Pilotage />} />
              <Route path="pilotage/board-pack" element={<BoardPack />} />
              <Route path="tasks" element={<TasksKanban />} />
              <Route path="legal" element={<DroitSocietes />} />
              <Route path="depenses/contrats" element={<EngagementsContrats />} />
              <Route path="depenses/contrats/nouveau" element={<EngagementImport />} />
              <Route path="depenses/contrats/:id" element={<EngagementContratDetail />} />
              <Route path="depenses/import" element={<EngagementImport />} />
              <Route path="depenses/calendrier" element={<EngagementsCalendrier />} />
              <Route path="depenses/resiliations" element={<EngagementsResiliationsList />} />
              <Route path="depenses/parametres" element={<EngagementsParametres />} />
              <Route path="invoices/new" element={<SalesDocumentEditor />} />
              <Route path="invoices/import" element={<SalesInvoiceImportUpload />} />
              <Route path="invoices/rebilling" element={<RebillingWizard />} />
              <Route path="invoices/import-batch" element={<Navigate to="/app/invoices?tab=import" replace />} />
              <Route path="invoices/import-batch-legacy" element={<ImportVentesBatch />} />
              <Route path="invoices/recurring" element={<Navigate to="/app/invoices?tab=recurring" replace />} />
              <Route path="invoices/recurring-legacy" element={<RecurringInvoices />} />
              <Route path="import-jobs" element={<Navigate to="/app/invoices?tab=import" replace />} />
              <Route path="invoices/numbering" element={<Navigate to="/app/invoices/settings?tab=numbering" replace />} />
              <Route path="invoices/history" element={<HistoriqueVentes />} />
              <Route path="invoices/:id/edit" element={<SalesDocumentEditor />} />
              <Route path="invoices/:id" element={<InvoiceView />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="quotes/:id" element={<SalesDocumentEditor />} />
              <Route path="analyse-ca" element={<AnalyseCA />} />
              <Route path="vigilance" element={<Vigilance />} />
              <Route path="achats-old-route" element={<div />} />
              <Route path="purchases" element={<PurchasesOverview />} />
              <Route path="purchases/list" element={<PurchasesList />} />
              <Route path="purchases/supplier-invoices" element={<PurchasesHub />} />
              <Route path="purchases/supplier-invoices/new" element={<SupplierInvoiceUpload />} />
              <Route path="purchases/supplier-invoices/:id" element={<SupplierInvoiceView />} />
              <Route path="purchases/import-batch" element={<Navigate to="/app/purchases/supplier-invoices?tab=import" replace />} />
              <Route path="purchases/import-batch-legacy" element={<ImportAchatsBatch />} />
              <Route path="purchases/history" element={<Navigate to="/app/purchases/supplier-invoices?tab=history" replace />} />
              <Route path="purchases/history-legacy" element={<HistoriqueAchats />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="suppliers/dedup" element={<SuppliersDedup />} />
              <Route path="suppliers/:id" element={<SupplierDetail />} />
              <Route path="purchases/vigilance" element={<VigilanceAchats />} />
              <Route path="contracts" element={<ContratsDashboard />} />
              <Route path="contracts/intake" element={<ContractIntake />} />
              <Route path="contracts/corbeille" element={<ContratsCorbeille />} />
              <Route path="settings/notifications" element={<NotificationSettings />} />
              <Route path="contracts/bulk-import" element={<Navigate to="/app/contracts/annuaire?tab=import" replace />} />
              <Route path="contracts/conformite" element={<Navigate to="/app/contracts/qualite?tab=conformite" replace />} />
              <Route path="contracts/chat" element={<ContratsChatCFO />} />
              <Route path="contracts/compare" element={<ContratsCompare />} />
              <Route path="contracts/anomalies" element={<Navigate to="/app/contracts/qualite?tab=anomalies" replace />} />
              <Route path="contracts/indexation" element={<Navigate to="/app/contracts/qualite?tab=indexation" replace />} />
              <Route path="contracts/portefeuille" element={<Navigate to="/app/contracts" replace />} />
              <Route path="contracts/echeances" element={<Navigate to="/app/contracts/annuaire?tab=echeances" replace />} />
              <Route path="contracts/financier" element={<Navigate to="/app/contracts" replace />} />
              <Route path="contracts/evolution" element={<Navigate to="/app/contracts" replace />} />
              <Route path="contracts/portefeuille-legacy" element={<ContratsPortefeuille />} />
              <Route path="contracts/financier-legacy" element={<ContratsFinancier />} />
              <Route path="contracts/evolution-legacy" element={<ContratsEvolution />} />
              <Route path="contracts/annuaire" element={<ContractsHub />} />
              <Route path="contracts/qualite" element={<ContractsQualityHub />} />
              <Route path="contracts/renewals" element={<Navigate to="/app/contracts/annuaire?tab=renewals" replace />} />
              <Route path="contracts/vigilance" element={<Navigate to="/app/contracts/qualite?tab=vigilance" replace />} />
              <Route path="contracts/parametres" element={<ContratsParametres />} />
              <Route path="contracts/:id" element={<ContractDetail />} />
              <Route path="expenses" element={<ExpenseReportsList />} />
              <Route path="expenses/new" element={<ExpenseReportEditor />} />
              <Route path="expenses/:id" element={<ExpenseReportView />} />
              <Route path="settings/thresholds" element={<ThresholdsSettings />} />
              <Route path="settings/billing" element={<TenantSettings />} />
              <Route path="settings/reminders" element={<ReminderSettings />} />
              <Route path="settings/backup-alerts" element={<BackupAlerts />} />
              <Route path="settings/security" element={<SecuritySettings />} />
              <Route path="settings/tenant-security" element={<TenantSecurity />} />
              <Route path="settings/api" element={<ApiSettings />} />
              <Route path="assistant" element={<ClarixAI />} />
              <Route path="reconciliation" element={<Reconciliation />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings/connectors" element={<Connectors />} />
              <Route path="entities" element={<Entities />} />
              <Route path="pending-actions" element={<PendingActions />} />
              <Route path="treasury" element={<Treasury />} />
            </Route>

            {/* Staff back-office (portail séparé) */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route
              path="/staff"
              element={
                <StaffProtectedRoute>
                  <StaffPortal />
                </StaffProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
          <GlobalUpsellHandler />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </StaffProvider>
    </BrowserRouter>
  );
}

export default App;
